import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Eye, EyeOff, LockKeyhole } from 'lucide-react';
import './LoginLanding.css';

export default function LoginLanding({ departments, onLogin, onBlocked, disabled = false, loading = false, loadError = '', onRetry }) {
  const [selectedId, setSelectedId] = useState(null);
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const passwordInput = useRef(null);
  const mounted = useRef(false);
  const visible = departments.filter(department => department.visible_on_homepage !== false);
  const selected = visible.length === 1 ? visible[0] : visible.find(department => department.id === selectedId);
  const blocked = selected?.access_blocked === true;
  const config = window.APP_CONFIG || {};
  const stationName = (config.title || document.title || 'Feuerwache').replace(/^Kantine\s+/i, '').replace(/\s+Kantine$/i, '').trim() || 'Feuerwache';

  useEffect(() => {
    mounted.current = true;
    document.body.classList.add('has-login-landing');
    return () => {
      mounted.current = false;
      document.body.classList.remove('has-login-landing');
    };
  }, []);

  useEffect(() => {
    setPassword(''); setRevealed(false); setError('');
  }, [selected?.id, blocked]);

  useEffect(() => {
    if (selectedId && !blocked && !disabled) passwordInput.current?.focus();
  }, [selectedId, blocked, disabled]);

  const selectDepartment = department => {
    if (disabled || inFlight.current) return;
    setSelectedId(department.id);
    if (department.access_blocked) onBlocked(department);
  };

  const submit = async event => {
    event.preventDefault();
    if (disabled || loading || loadError || inFlight.current || !selected || blocked || !password) return;
    inFlight.current = true;
    setPending(true); setError('');
    try {
      await onLogin(selected, password);
    } catch (requestError) {
      if (mounted.current) setError(requestError.message || 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      inFlight.current = false;
      if (mounted.current) {
        setPending(false); setPassword(''); setRevealed(false);
        passwordInput.current?.focus();
      }
    }
  };

  return (
    <main className="login-landing">
      <section className="login-landing__card" aria-label="Kantinenanmeldung">
        <header className="login-landing__identity">
          <img src={config.logo_base64 || '/logo.png'} alt={`Wachlogo ${stationName}`} />
          <p className="login-landing__eyebrow">Kantine</p>
          <h1>{stationName}</h1>
        </header>
        <div className="login-landing__form">
          <h2>Anmelden</h2>
          {loading ? <p role="status">Wachabteilungen werden geladen …</p> : loadError ? (
            <div role="alert"><p>{loadError}</p><button className="login-landing__retry" type="button" onClick={onRetry}>Erneut versuchen</button></div>
          ) : (
            <>
              {visible.length > 1 && (
                <div className="login-landing__selection">
                  <p id="login-departments-label" className="login-landing__label">Wachabteilung</p>
                  <div role="group" aria-labelledby="login-departments-label" className="login-landing__departments">
                    {visible.map(department => (
                      <button key={department.id} type="button" data-department-id={department.id}
                        aria-pressed={selected?.id === department.id} disabled={disabled || pending}
                        onClick={() => selectDepartment(department)}>
                        <span>{department.name}</span>
                        {department.access_blocked && <span className="login-landing__blocked-badge"><LockKeyhole size={12} aria-hidden="true" /> Deaktiviert</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {blocked ? (
                <div className="login-landing__blocked">
                  <p>Der Zugang zu dieser Kantine ist derzeit deaktiviert.</p>
                  <button type="button" data-blocked-access className="login-landing__retry" disabled={disabled} onClick={() => onBlocked(selected)}>Informationen zur Freischaltung</button>
                </div>
              ) : (
                <form onSubmit={submit} aria-busy={pending}>
                  {!visible.length && <p role="status" className="login-landing__empty">Derzeit ist keine Wachabteilung verfügbar.</p>}
                  <label htmlFor="department-login-password" className="login-landing__label">Passwort{visible.length > 1 && selected ? ` · ${selected.name}` : ''}</label>
                  <div className="login-landing__password">
                    <input ref={passwordInput} id="department-login-password" type={revealed ? 'text' : 'password'} autoComplete="current-password"
                      value={password} onChange={event => setPassword(event.target.value)} disabled={disabled || pending || !visible.length}
                      aria-invalid={Boolean(error)} aria-describedby={error ? 'department-login-error' : undefined} required />
                    <button type="button" onClick={() => setRevealed(value => !value)} disabled={disabled || pending || !visible.length}
                      aria-label={revealed ? 'Passwort verbergen' : 'Passwort anzeigen'} aria-pressed={revealed}>
                      {revealed ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                    </button>
                  </div>
                  {error && <p className="login-landing__error" id="department-login-error" role="alert">{error}</p>}
                  <button className="login-landing__submit" type="submit" disabled={disabled || pending || !selected || !password}>
                    {pending ? 'Anmeldung läuft …' : 'Anmelden'}<ArrowRight size={19} aria-hidden="true" />
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </section>
    </main>
  );
}
